import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, PieChart, Building2, Store, Bike, FileText, LogOut, Banknote, Share2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCityTheme } from "@/context/CityThemeContext";
import { TimeFoodLogo, CityLogo, CityBrandWordmark } from "@/components/Brand";
import { NotificationBell } from "@/components/Shell";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const useAdminTheme = () => {
  const { user } = useAuth();
  const { cityName } = useCityTheme();
  const isCity = user?.scope === "city";
  return {
    isCity,
    accent: isCity ? "#1F3864" : "#FF7A1A",
    viewLabel: isCity ? `Visão Prefeitura de ${cityName}` : "Visão Time Food · todas as cidades",
  };
};

const LINKS = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, testId: "admin-nav-dashboard" },
  { to: "/admin/financeiro", label: "Financeiro", icon: PieChart, testId: "admin-nav-finance" },
  { to: "/admin/cidades", label: "Cidades", icon: Building2, testId: "admin-nav-cities", platformOnly: true },
  { to: "/admin/lojistas", label: "Lojistas", icon: Store, testId: "admin-nav-stores" },
  { to: "/admin/motoboys", label: "Motoboys", icon: Bike, testId: "admin-nav-couriers" },
  { to: "/admin/indicacoes", label: "Indicações", icon: Share2, testId: "admin-nav-referrals" },
  { to: "/admin/saques", label: "Saques & KYC", icon: Banknote, testId: "admin-nav-withdrawals", platformOnly: true },
  { to: "/admin/arrecadacao", label: "Arrecadação", icon: FileText, testId: "admin-nav-report" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { isCity, accent, viewLabel } = useAdminTheme();
  const { cities, city, cityId, setCity, cityName } = useCityTheme();
  const navigate = useNavigate();
  const links = LINKS.filter((l) => !l.platformOnly || !isCity);

  return (
    <div className="min-h-screen bg-gf-gray" data-testid="admin-layout">
      <header className="text-white" style={{ background: isCity ? "#1F3864" : "#141414" }}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="bg-white rounded-xl p-1.5 shrink-0">
              <CityLogo city={city} size={26} showText={false} />
            </div>
            <div className="h-8 w-px bg-white/25 hidden sm:block" />
            <TimeFoodLogo size={24} light />
            <div className="hidden md:block pl-3 border-l border-white/20">
              <p className="text-[11px] font-bold uppercase tracking-wider text-white/70">Painel administrativo</p>
              <p className="text-xs font-semibold" data-testid="admin-view-label">{viewLabel}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!isCity && (
              <div data-testid="presentation-city-switcher">
                <Select value={cityId} onValueChange={setCity}>
                  <SelectTrigger className="h-9 w-[130px] sm:w-[190px] rounded-xl bg-white/10 border-white/20 text-white text-xs"
                    data-testid="presentation-city-select">
                    <SelectValue placeholder="Cidade da apresentação" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.brand_name || c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="text-white"><NotificationBell accent={accent} /></div>
            <div className="text-right hidden lg:block">
              <p className="text-xs font-bold leading-none">{user?.name}</p>
              <p className="text-[10px] text-white/60">{user?.email}</p>
            </div>
            <button data-testid="logout-button" onClick={async () => { await logout(); navigate("/login"); }}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 gf-press">
              <LogOut size={17} />
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 pb-2 flex items-center justify-between gap-2">
          <p className="text-[11px] font-semibold text-white/80 md:hidden">{viewLabel}</p>
          <p className="text-[11px] text-white/70">
            Marca em exibição: <b className="text-white">{city?.brand_name || `${cityName} Time Food`}</b>
          </p>
        </div>
      </header>

      <nav className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-2 flex gap-1 overflow-x-auto no-scrollbar">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.end} data-testid={l.testId}
              className="flex items-center gap-2 px-3 py-3.5 text-sm font-bold shrink-0 relative"
              style={({ isActive }) => ({ color: isActive ? accent : "#6B7280" })}>
              {({ isActive }) => (
                <>
                  <l.icon size={16} /> {l.label}
                  {isActive && <span className="absolute bottom-0 left-2 right-2 h-[3px] rounded-full" style={{ background: accent }} />}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-6"><Outlet /></main>

      <footer className="max-w-7xl mx-auto px-4 py-8 flex items-center gap-3 border-t border-gray-200 mt-6 flex-wrap">
        <CityLogo city={city} size={30} showText={false} />
        <div className="h-8 w-px bg-gray-300" />
        <CityBrandWordmark city={cityName} size={15} accent={accent} />
        <p className="text-[10px] text-gray-400 ml-auto text-right">
          Parceria oficial — Prefeitura de {cityName}<br />software Time Food
        </p>
      </footer>
    </div>
  );
}
